import { CheckCircle2, ChevronRight } from 'lucide-react';
import { cn } from '../../lib/utils';

export interface StepTabItem {
  value: string;
  label: string;
  /** Step has been finished. */
  done?: boolean;
  /** Optional badge node (e.g. a count) shown after the label. */
  badge?: React.ReactNode;
}

interface Props {
  steps: StepTabItem[];
  activeValue: string;
  onSelect: (value: string) => void;
  className?: string;
}

/**
 * Visual stepper that doubles as a tab list. Pairs nicely with the project's
 * `Tabs` root: pass `activeMainTab` and `setActiveMainTab` from the parent.
 *
 * Renders chevron-shaped pills with checkmarks on completed steps, an accent
 * underline on the current step, and muted styling for upcoming steps.
 */
export default function StepTabsList({ steps, activeValue, onSelect, className }: Props) {
  return (
    <div
      role="tablist"
      aria-label="Campaign steps"
      className={cn(
        'flex flex-nowrap items-stretch gap-1 overflow-x-auto overscroll-x-contain pb-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden',
        'md:flex-wrap md:overflow-visible md:pb-0',
        className,
      )}
    >
      {steps.map((step, idx) => {
        const isActive = step.value === activeValue;
        // Anything before the active step is considered "passed" for visual
        // ordering, regardless of its `done` flag, so the trail reads left-to-right.
        const passed = !isActive && (step.done || steps.findIndex((s) => s.value === activeValue) > idx);
        return (
          <div key={step.value} className="flex shrink-0 items-center">
            <button
              type="button"
              role="tab"
              aria-selected={isActive}
              onClick={() => onSelect(step.value)}
              className={cn(
                'group flex min-h-10 shrink-0 items-center gap-1.5 whitespace-nowrap rounded-md border px-3 py-1.5 text-sm font-medium transition-all md:min-h-0',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#01696f]/40',
                isActive
                  ? 'border-[#01696f] bg-[#01696f] text-white shadow-sm dark:border-teal-500 dark:bg-teal-700'
                  : passed
                    ? 'border-emerald-300 bg-emerald-50 text-emerald-900 hover:bg-emerald-100 dark:border-emerald-800 dark:bg-emerald-950/30 dark:text-emerald-200 dark:hover:bg-emerald-950/50'
                    : 'border-[#d4d1ca] bg-white text-[#77756f] hover:border-[#a8a6a1] hover:text-[#22211d] dark:border-gray-700 dark:bg-gray-900 dark:text-gray-400 dark:hover:text-gray-100',
              )}
            >
              <span
                className={cn(
                  'inline-flex h-5 min-w-5 items-center justify-center rounded-full text-xs font-semibold',
                  isActive
                    ? 'bg-white/20 text-white'
                    : passed
                      ? 'bg-emerald-200 text-emerald-900 dark:bg-emerald-800 dark:text-emerald-100'
                      : 'bg-[#f0eeea] text-[#77756f] dark:bg-gray-800 dark:text-gray-400',
                )}
                aria-hidden
              >
                {passed ? <CheckCircle2 className="h-3.5 w-3.5" /> : idx + 1}
              </span>
              <span>{step.label}</span>
              {step.badge && <span className="ml-1">{step.badge}</span>}
            </button>
            {idx < steps.length - 1 && (
              <ChevronRight className="mx-0.5 h-4 w-4 shrink-0 text-[#a8a6a1] dark:text-gray-600" aria-hidden />
            )}
          </div>
        );
      })}
    </div>
  );
}
